/**
 * Reports View (Custom Report Engine — authoring landing)
 *
 * super_admin-only (the whole /admin tree is). Lets a platform operator pick a
 * TARGET TENANT and manage that tenant's custom report specs: list, create,
 * edit, delete, and quick-run/preview. All engine calls carry the target tenant
 * via the X-Tenant-ID header (reportEngineService), gated server-side by the
 * tenant's platform_support_access consent.
 *
 * The selected scope (a tenant UUID, or 'global') is carried into the authoring
 * page via ?scope=<scope>.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { tenantsService, reportEngineService } from '../../services/api';
import { formatDate } from '../../utils/formatters';

const ARCHETYPE_LABEL = { pivot: 'Pivot', listing: 'Listing', hierarchy: 'Hierarchy' };
const STATUS_LABEL = { draft: 'Draft', published: 'Published', archived: 'Archived' };
const GLOBAL = 'global';

const ReportsView = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  // scope: '' (none), 'global', or a tenant UUID.
  const [scope, setScope] = useState('');
  const [specs, setSpecs] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingSpecs, setLoadingSpecs] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [cloneSpec, setCloneSpec] = useState(null);
  const [cloneTargetTenant, setCloneTargetTenant] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [cloneStatus, setCloneStatus] = useState('draft');
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await tenantsService.getAll();
        // Exclude the platform tenant itself — reports are authored for clients.
        const list = (res.data || []).filter(
          (t) => t.id !== '00000000-0000-0000-0000-000000000001'
        );
        setTenants(list);
      } catch (err) {
        console.error('Failed to load tenants', err);
        setError('Failed to load tenants.');
      } finally {
        setLoadingTenants(false);
      }
    })();
  }, []);

  const loadSpecs = useCallback(async (sc) => {
    if (!sc) { setSpecs([]); return; }
    setLoadingSpecs(true);
    setError(null);
    try {
      if (sc === GLOBAL) {
        // Global view: list with no target tenant, show only globals (tenant_id null).
        const res = await reportEngineService.listSpecs(null);
        setSpecs((res.data || []).filter((s) => s.tenant_id === null));
      } else {
        // Tenant view: that tenant's own specs + globals (backend returns both).
        const res = await reportEngineService.listSpecs(sc);
        setSpecs(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load specs', err);
      setError('Failed to load report specs.');
      setSpecs([]);
    } finally {
      setLoadingSpecs(false);
    }
  }, []);

  useEffect(() => { loadSpecs(scope); }, [scope, loadSpecs]);

  // A spec's own scope param for edit/delete: global if tenant_id is null,
  // otherwise its owning tenant.
  const specScope = (s) => (s.tenant_id === null ? GLOBAL : s.tenant_id);

  const handleDelete = async (spec) => {
    if (!window.confirm('Delete report "' + spec.name + '"? This cannot be undone.')) return;
    try {
      const sc = specScope(spec);
      await reportEngineService.deleteSpec(sc === GLOBAL ? null : sc, spec.id);
      setNotice('Deleted "' + spec.name + '".');
      loadSpecs(scope);
    } catch (err) {
      console.error('Delete failed', err);
      setError('Failed to delete the report.');
    }
  };

  // New report uses the current picker scope; editing uses the spec's own scope.
  // NOTE: the URL param is `scope` (NOT `tenant`) on purpose — the Onboarding
  // axios interceptor reads ?tenant= on localhost and would inject it as the
  // X-Tenant-ID header, sending a bogus 'global' tenant to the API. Using a
  // distinct param name keeps scope-passing separate from tenant detection.
  const goAuthor = (specId, specScopeValue) => {
    const sc = specScopeValue || scope;
    const base = specId ? ('/admin/reports/' + specId) : '/admin/reports/new';
    navigate(base + '?scope=' + encodeURIComponent(sc));
  };

  const openClone = (spec) => {
    setCloneSpec(spec);
    setCloneTargetTenant(scope !== GLOBAL ? scope : (tenants[0]?.id || ''));
    setCloneName(spec.name);
    setCloneStatus('draft');
    setError(null);
  };

  const handleClone = async () => {
    if (!cloneSpec || !cloneTargetTenant) {
      setError('Pick a target tenant for the clone.');
      return;
    }
    setCloning(true);
    setError(null);
    try {
      await reportEngineService.cloneSpec(cloneSpec.id, {
        tenant_id: cloneTargetTenant,
        name: cloneName.trim() || undefined,
        status: cloneStatus,
      });
      setNotice('Cloned "' + cloneSpec.name + '" to target tenant.');
      setCloneSpec(null);
    } catch (err) {
      console.error('Clone failed', err);
      setError(err?.response?.data?.message || 'Clone failed.');
    } finally {
      setCloning(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-success mb-3">
          <Icons.CheckCircle size={18} />
          <span>{notice}</span>
        </div>
      )}

      {/* Scope picker (a tenant, or Global) */}
      <div className="card mb-3" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
              Authoring reports for
            </label>
            <select
              className="form-control"
              value={scope}
              onChange={(e) => { setScope(e.target.value); setNotice(null); }}
              disabled={loadingTenants}
            >
              <option value="">{loadingTenants ? 'Loading tenants…' : 'Select a tenant or scope…'}</option>
              <option value={GLOBAL}>🌐 Global (all tenants)</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => loadSpecs(scope)}
            disabled={!scope || loadingSpecs}
          >
            <Icons.RefreshCw size={18} />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => goAuthor(null)}
            disabled={!scope}
          >
            <Icons.Plus size={18} />
            New Report
          </button>
        </div>
      </div>

      {/* Spec list */}
      {!scope ? (
        <div className="card">
          <div className="empty-state">
            <Icons.BarChart size={64} />
            <h3>Select a scope</h3>
            <p>Choose a tenant (to author its reports + see globals) or Global (all-tenant reports) above.</p>
          </div>
        </div>
      ) : loadingSpecs ? (
        <div className="empty-state">
          <Icons.Loader size={48} className="spin" />
          <p>Loading reports…</p>
          <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <div className="card">
          {specs.length === 0 ? (
            <div className="empty-state">
              <Icons.FileText size={64} />
              <h3>No reports yet</h3>
              <p>{scope === GLOBAL ? 'No global reports yet. Create the first one.' : 'No reports for this scope yet. Create the first one.'}</p>
              <button className="btn btn-primary mt-2" onClick={() => goAuthor(null)}>
                <Icons.Plus size={18} />
                New Report
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Scope</th>
                    <th>Updated</th>
                    <th style={{ width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {specs.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{s.name}</div>
                      </td>
                      <td>
                        <span className="badge badge-neutral">{s.report_type || ARCHETYPE_LABEL[s.archetype] || s.archetype}</span>
                      </td>
                      <td>
                        <span className={'badge ' + (s.status === 'published' ? 'badge-success' : 'badge-neutral')}>
                          {STATUS_LABEL[s.status] || s.status || 'published'}
                        </span>
                      </td>
                      <td>
                        {s.tenant_id === null
                          ? <span className="badge badge-success">🌐 Global</span>
                          : <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Tenant</span>}
                      </td>
                      <td>{formatDate(s.updated_at || s.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => goAuthor(s.id, specScope(s))}
                            title="Edit"
                          >
                            <Icons.Edit size={14} />
                          </button>
                          {s.tenant_id && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openClone(s)}
                              title="Clone to another tenant"
                            >
                              <Icons.Copy size={14} />
                            </button>
                          )}
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDelete(s)}
                            title="Delete"
                          >
                            <Icons.Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {cloneSpec && (
        <div className="card mt-3" style={{ padding: '16px', border: '1px solid var(--border-color, #e5e7eb)' }}>
          <h3 style={{ marginTop: 0 }}>Clone to tenant</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Copy &quot;{cloneSpec.name}&quot; (report type, spec, and default runtime) to another tenant.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ minWidth: '240px', flex: 1 }}>
              <label className="form-label">Target tenant</label>
              <select className="form-control" value={cloneTargetTenant} onChange={(e) => setCloneTargetTenant(e.target.value)}>
                <option value="">Select tenant…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '240px', flex: 1 }}>
              <label className="form-label">Report name</label>
              <input className="form-control" value={cloneName} onChange={(e) => setCloneName(e.target.value)} />
            </div>
            <div style={{ minWidth: '160px' }}>
              <label className="form-label">Initial status</label>
              <select className="form-control" value={cloneStatus} onChange={(e) => setCloneStatus(e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={handleClone} disabled={cloning}>
              {cloning ? 'Cloning…' : 'Clone report'}
            </button>
            <button className="btn btn-secondary" onClick={() => setCloneSpec(null)} disabled={cloning}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsView;
